import { ActionFiringMode } from "../flatbuffers/generated/archive/action-firing-mode";

/**
 * How a floor-targeting control command is dispatched, resolved across the
 * three things that can decide it: the user, their organization, and — when
 * neither has an opinion — the surface itself.
 *
 * The two surfaces disagreed by accident. The Car Calls pad sends on the first
 * floor-button click; an Actions card stages the floor and waits for a second
 * press. One is faster, the other cannot send a command at a floor nobody
 * chose, and which a site wants is a property of the site.
 */

/** The two places a floor-targeting command can be sent from. */
export enum FiringSurface {
  CallPad = "CALL_PAD",
  ActionCard = "ACTION_CARD",
}

/**
 * What each surface does when nothing has been configured.
 *
 * Deliberately NOT one shared default. A single default has to change one of
 * the two surfaces on upgrade, and changing either is a live behaviour change
 * to elevator controls that no operator asked for: the pad has always sent on
 * the first click, and a card has always staged. These values are simply what
 * each surface already does, so an upgrade changes nothing until somebody sets
 * the option deliberately.
 */
const SURFACE_DEFAULT: Record<FiringSurface, ActionFiringMode> = {
  [FiringSurface.CallPad]: ActionFiringMode.ONE_CLICK,
  [FiringSurface.ActionCard]: ActionFiringMode.TWO_STEP,
};

/** A value that may arrive as the enum, its name, or absent. */
export type ActionFiringModeInput = ActionFiringMode | string | null | undefined;

/**
 * Coerce a wire or JSON value into the enum.
 *
 * Accepts the numeric ordinal (FlatBuffer) and the label (JSON), because the
 * same setting reaches the app both ways. Anything unrecognised resolves to
 * INHERIT rather than to a behaviour: a value nobody recognises must defer, not
 * pick a side.
 */
export function toActionFiringMode(value: ActionFiringModeInput): ActionFiringMode {
  if (typeof value === "number") {
    return value === ActionFiringMode.ONE_CLICK || value === ActionFiringMode.TWO_STEP
      ? value
      : ActionFiringMode.INHERIT;
  }

  if (value === "ONE_CLICK") return ActionFiringMode.ONE_CLICK;
  if (value === "TWO_STEP") return ActionFiringMode.TWO_STEP;

  return ActionFiringMode.INHERIT;
}

/**
 * What the operator has CONFIGURED, across the user and organization levels.
 *
 * Returns INHERIT when neither level has an explicit value — that is a real
 * answer, meaning "nobody has chosen", and it is what lets each surface keep
 * its own default. Resolving to a behaviour here instead would force one
 * default on both surfaces, which is the upgrade hazard above.
 */
export function resolveConfiguredMode(
  user: ActionFiringModeInput,
  org: ActionFiringModeInput,
): ActionFiringMode {
  const userMode = toActionFiringMode(user);
  if (userMode !== ActionFiringMode.INHERIT) return userMode;

  return toActionFiringMode(org);
}

/**
 * The mode a given surface will actually use.
 *
 * A configured value applies to BOTH surfaces — that is the point of the
 * setting. Only when nothing is configured does the surface fall back to what
 * it has always done.
 */
export function effectiveFiringMode(
  configured: ActionFiringModeInput,
  surface: FiringSurface,
): ActionFiringMode {
  const mode = toActionFiringMode(configured);

  return mode === ActionFiringMode.INHERIT ? SURFACE_DEFAULT[surface] : mode;
}

/**
 * True when a floor button on this surface should send immediately.
 *
 * The single predicate every call site uses, so no surface can drift into
 * comparing against the enum itself and getting the INHERIT case wrong.
 */
export function firesOnFloorClick(
  configured: ActionFiringModeInput,
  surface: FiringSurface,
): boolean {
  return effectiveFiringMode(configured, surface) === ActionFiringMode.ONE_CLICK;
}
